import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Heart } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1544027993-37dbfe43562a?auto=format&fit=crop&w=1200&q=80";

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const scrollToId = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(id, { offset: -72, duration: 1.4 });
  } else {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden pt-[72px]">
      <div className="absolute -top-32 -right-32 w-[540px] h-[540px] rounded-full bg-sand/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-sage/20 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center w-full">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-xs md:text-sm font-semibold uppercase tracking-[0.22em] text-terra mb-8"
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1 className="font-serif font-medium tracking-tight text-ink text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[1.02]">
            <RevealLine delay={0.25}>{t.hero.line1}</RevealLine>
            <RevealLine delay={0.4}>{t.hero.line2}</RevealLine>
            <RevealLine delay={0.55}>
              <em className="text-terra">{t.hero.line3}</em>
            </RevealLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-inksoft"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-structures"
              onClick={() => scrollToId("#strutture")}
              className="group inline-flex items-center gap-2 bg-terra hover:bg-terradark text-cream text-sm font-semibold px-7 py-3.5 rounded-full transition-colors duration-300"
            >
              {t.hero.ctaPrimary}
              <ArrowDown size={16} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-mission"
              onClick={() => scrollToId("#missione")}
              className="inline-flex items-center gap-2 border border-ink/20 hover:border-terra hover:text-terra text-ink text-sm font-semibold px-7 py-3.5 rounded-full transition-colors duration-300"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.3 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-3"
          >
            {[t.hero.stat1, t.hero.stat2, t.hero.stat3].map((s, i) => (
              <div key={i} className="flex items-center gap-2.5 text-sm text-inksoft">
                <span className="w-1.5 h-1.5 rounded-full bg-terra" />
                {s}
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-[0_30px_80px_rgba(45,50,43,0.18)]"
          >
            <motion.img
              src={HERO_IMG}
              alt="Mani che si sostengono a vicenda"
              style={{ y: imgY }}
              className="w-full h-[115%] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent" />
          </motion.div>

          <motion.div
            data-testid="hero-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="absolute -bottom-6 -left-6 md:-left-10 bg-white rounded-2xl shadow-[0_16px_50px_rgba(45,50,43,0.14)] px-6 py-4 flex items-center gap-3"
          >
            <span className="w-10 h-10 rounded-full bg-terra/10 flex items-center justify-center">
              <Heart size={18} strokeWidth={1.5} className="text-terra" />
            </span>
            <span className="font-serif text-lg text-ink">{t.hero.badge}</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
